'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Play, Pause, X, RotateCcw } from 'lucide-react';
import { getImageUrl } from '@/lib/api';
import { initGPT, injectAd } from '@/lib/gpt-loader';
import { API_PROXY_PATH } from '@/lib/constants';
import { processArticleLinks } from '@/lib/linkUtils';
import { useWebsiteSetting } from '@/components/ads/AdDataProvider';
interface Slide {
  imageUrl?: string;
  videoUrl?: string;
  title: string;
  isVideo: boolean;
  isAd?: boolean;
  adCode?: string | null;
  adIndex?: number;
  attribution?: string;
  ctaTitle?: string;
  ctaUrl?: string;
  focusX?: number;
  focusY?: number;
  authorName?: string;
  promotionalMessage?: string;
  isLastCard?: boolean;
}
const VIDEO_BASE_URL = 'https://video.assettype.com';
interface StoryData {
  slug: string;
  story: any;
}
interface VisualStoryProps {
  story: any;
  onClose?: () => void;
  sliderAdCode?: string | null;
}

// Image cache to track loaded images
const imageCache = new Set<string>();

// Preload image function
const preloadImage = (url: string): Promise<void> => {
  if (!url || imageCache.has(url)) return Promise.resolve();
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      imageCache.add(url);
      resolve();
    };
    img.onerror = () => resolve();
    img.src = url;
  });
};

// Component to render slider ad with dynamic div IDs
interface SliderAdSlotProps {
  adCode: string | null;
  adIndex: number;
}

/**
 * Extract GPT ad slot path from ad code.
 * Handles both AMP format (amp-story-auto-ads with JSON config)
 * and regular GPT format (defineSlot script).
 */
function extractAdSlotPath(adCode: string): string | null {
  // Try AMP format: <script type="application/json">{"ad-attributes":{"data-slot":"/3167926/..."}}
  const jsonMatch = adCode.match(/"data-slot"\s*:\s*"([^"]+)"/);
  if (jsonMatch) return jsonMatch[1];

  // Try regular GPT format: defineSlot('/3167926/...', ...)
  const gptMatch = adCode.match(/defineSlot\s*\(\s*['"]([^'"]+)['"]/);
  if (gptMatch) return gptMatch[1];
  return null;
}
function SliderAdSlot({
  adCode,
  adIndex
}: SliderAdSlotProps) {
  const adContainerRef = useRef<HTMLDivElement>(null);
  const executedRef = useRef(false);
  const divId = `div-gpt-vs-ad-${adIndex}-${Date.now()}`;
  useEffect(() => {
    if (!adCode || !adContainerRef.current || executedRef.current) {
      return;
    }
    const slotPath = extractAdSlotPath(adCode);
    if (!slotPath) {
      executedRef.current = true;
      initGPT(() => {
        if (adContainerRef.current) {
          injectAd(adContainerRef.current, adCode, `SliderAdSlot-${adIndex}`);
        }
      });
      return;
    }
    executedRef.current = true;

    // Create the ad container div
    if (adContainerRef.current) {
      adContainerRef.current.innerHTML = '';
      const adDiv = document.createElement('div');
      adDiv.id = divId;
      adDiv.style.minWidth = '300px';
      adDiv.style.minHeight = '250px';
      adContainerRef.current.appendChild(adDiv);
    }

    // Use GPT defineSlot + display with the extracted slot path
    initGPT(() => {
      const googletag = (window as any).googletag;
      googletag.cmd.push(() => {
        try {
          const slot = googletag.defineSlot(slotPath, [[300, 250], [336, 280], [250, 250]], divId);
          if (slot) {
            slot.addService(googletag.pubads());
            googletag.enableServices();
            googletag.display(divId);
          }
        } catch (err) {}
      });
    });
    return () => {
      // Destroy just this slot on unmount
      const googletag_0 = (window as any).googletag;
      if (googletag_0?.destroySlots) {
        try {
          const slots = googletag_0.pubads?.()?.getSlots?.() || [];
          const thisSlot = slots.find((s: any) => s.getSlotElementId?.() === divId);
          if (thisSlot) {
            googletag_0.destroySlots([thisSlot]);
          }
        } catch {/* ignore */}
      }
      if (adContainerRef.current) {
        adContainerRef.current.innerHTML = '';
      }
    };
  }, [adCode, adIndex]);
  if (!adCode) {
    return null;
  }
  return <div className="w-full h-full flex items-center justify-center bg-black">
      <div ref={adContainerRef} className="flex items-center justify-center" />
    </div>;
}
export default function VisualStory({
  story: initialStory,
  onClose,
  sliderAdCode: propSliderAdCode
}: VisualStoryProps) {
  const [currentStory, setCurrentStory] = useState(initialStory);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [allStories, setAllStories] = useState<StoryData[]>([]);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [storiesNavigated, setStoriesNavigated] = useState(0);
  const [adSlotInterval, setAdSlotInterval] = useState<number>(0);
  const [sliderAdCode, setSliderAdCode] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const preloadedRef = useRef<Set<number>>(new Set());

  // Store the URL to return to when closing (the page user was on before opening story)
  const returnUrlRef = useRef<string | null>(null);

  // Read ad settings from context (embedded in HTML, zero HTTP calls)
  const adSlotAfterStoriesSetting = useWebsiteSetting('ad-slot-after-stories');
  useEffect(() => {
    if (adSlotAfterStoriesSetting?.value) {
      setAdSlotInterval(adSlotAfterStoriesSetting.value);
    }
    if (propSliderAdCode) {
      setSliderAdCode(propSliderAdCode);
    }
  }, [adSlotAfterStoriesSetting, propSliderAdCode]);

  // Capture return URL on mount (prioritize sessionStorage over referrer)
  useEffect(() => {
    if (!returnUrlRef.current) {
      // First priority: Check sessionStorage (set by Slider component)
      const storedUrl = sessionStorage.getItem('visualStoryReturnUrl');
      if (storedUrl) {
        returnUrlRef.current = storedUrl;
      } else {
        // Fallback: Try document.referrer
        const referrer = document.referrer;
        if (referrer && referrer.includes(window.location.host)) {
          try {
            const url = new URL(referrer);
            returnUrlRef.current = url.pathname;
          } catch {
            returnUrlRef.current = '/';
          }
        } else {
          returnUrlRef.current = '/';
        }
      }
    }
  }, []);

  // Build slides array from current story data
  const slides = useMemo(() => {
    const contentSlides: Slide[] = [];

    // First slide: hero image or video with headline
    const heroAttribution = currentStory['hero-image-attribution'] || '';
    const heroMeta = currentStory['hero-image-metadata'];
    const heroFocusPoint = heroMeta?.['focus-point'];
    const heroFocusX = heroFocusPoint && heroMeta?.width ? Math.round(heroFocusPoint[0] / heroMeta.width * 100) : undefined;
    const heroFocusY = heroFocusPoint && heroMeta?.height ? Math.round(heroFocusPoint[1] / heroMeta.height * 100) : undefined;
    // Get author name for first slide
    const authorName = currentStory.authors?.map((a: any) => a.name).join(', ') || currentStory['author-name'] || '';
    if (currentStory['hero-image-s3-key']) {
      contentSlides.push({
        imageUrl: getImageUrl(currentStory['hero-image-s3-key']),
        title: currentStory.headline || '',
        isVideo: false,
        attribution: heroAttribution,
        focusX: heroFocusX,
        focusY: heroFocusY,
        authorName
      });
    } else if (currentStory['hero-video-s3-key']) {
      contentSlides.push({
        videoUrl: `${VIDEO_BASE_URL}/${currentStory['hero-video-s3-key']}`,
        title: currentStory.headline || '',
        isVideo: true,
        attribution: heroAttribution,
        authorName
      });
    }

    // Get promotional message from story level
    const promotionalMessage = currentStory['promotional-message'] || '';

    // Subsequent slides: from cards -> story-elements
    if (currentStory.cards && Array.isArray(currentStory.cards)) {
      const cards = currentStory.cards;
      cards.forEach((card: any, cardIndex: number) => {
        const elements = card['story-elements'] || [];
        const isLastCard = cardIndex === cards.length - 1;
        let currentMedia: any = null;
        let allText: string[] = [];
        let ctaTitle: string | undefined;
        let ctaUrl: string | undefined;
        let hasPromotionalElement = false;

        // Helper to compute focus-point percentages from media element
        const getFocusPoint = (media: any) => {
          const imgMeta = media?.['image-metadata'];
          const fp = imgMeta?.['focus-point'];
          if (fp && Array.isArray(fp) && fp.length >= 2 && imgMeta?.width && imgMeta?.height) {
            return {
              focusX: Math.round(fp[0] / imgMeta.width * 100),
              focusY: Math.round(fp[1] / imgMeta.height * 100)
            };
          }
          return {};
        };
        elements.forEach((element: any) => {
          if (element['image-s3-key'] || element['video-s3-key']) {
            if (currentMedia) {
              const combinedText = allText.join(' ').trim();
              const mediaAttribution = currentMedia['image-attribution'] || '';
              const focus = getFocusPoint(currentMedia);
              if (currentMedia['video-s3-key']) {
                contentSlides.push({
                  videoUrl: `${VIDEO_BASE_URL}/${currentMedia['video-s3-key']}`,
                  title: combinedText,
                  isVideo: true,
                  attribution: mediaAttribution
                });
              } else if (currentMedia['image-s3-key']) {
                contentSlides.push({
                  imageUrl: getImageUrl(currentMedia['image-s3-key']),
                  title: combinedText,
                  isVideo: false,
                  attribution: mediaAttribution,
                  ...focus
                });
              }
            }
            currentMedia = element;
            allText = [];
            ctaTitle = undefined;
            ctaUrl = undefined;
          }
          // CTA element - extract title/url for button rendering
          else if (element.subtype === 'cta' && element.metadata) {
            ctaTitle = element.metadata['cta-title'] || '';
            ctaUrl = element.metadata['cta-url'] || '';
          }
          // Skip promotional message text from regular text content
          else if (element.metadata?.['promotional-message']) {
            hasPromotionalElement = true;
          } else if (element.text) {
            allText.push(element.text);
          }
        });
        if (currentMedia) {
          const combinedText_0 = allText.join(' ').trim();
          const mediaAttribution_0 = currentMedia['image-attribution'] || '';
          const focus_0 = getFocusPoint(currentMedia);
          if (currentMedia['video-s3-key']) {
            contentSlides.push({
              videoUrl: `${VIDEO_BASE_URL}/${currentMedia['video-s3-key']}`,
              title: combinedText_0,
              isVideo: true,
              attribution: mediaAttribution_0,
              ctaTitle,
              ctaUrl,
              isLastCard,
              promotionalMessage: isLastCard ? promotionalMessage : undefined
            });
          } else if (currentMedia['image-s3-key']) {
            contentSlides.push({
              imageUrl: getImageUrl(currentMedia['image-s3-key']),
              title: combinedText_0,
              isVideo: false,
              attribution: mediaAttribution_0,
              ctaTitle,
              ctaUrl,
              isLastCard,
              promotionalMessage: isLastCard ? promotionalMessage : undefined,
              ...focus_0
            });
          }
        }
      });
    }

    // Insert ad slides after every N content slides
    if (adSlotInterval > 0 && sliderAdCode) {
      const finalSlides: Slide[] = [];
      let adIndex = 0;
      contentSlides.forEach((slide, index) => {
        finalSlides.push(slide);

        // Insert ad after every N slides (N = adSlotInterval)
        // If adSlotInterval is 5, ads appear after indices 4, 9, 14... (after every 5th slide)
        if ((index + 1) % adSlotInterval === 0 && index < contentSlides.length - 1) {
          finalSlides.push({
            title: '',
            isVideo: false,
            isAd: true,
            adCode: sliderAdCode,
            adIndex: adIndex
          });
          adIndex++;
        }
      });
      return finalSlides;
    }
    return contentSlides;
  }, [currentStory, adSlotInterval, sliderAdCode]);

  // Preload current and adjacent slides with higher priority
  useEffect(() => {
    const preloadSlides = async () => {
      // Prioritize current, next, and previous slides
      const slidesToPreload = [currentSlide, currentSlide + 1, currentSlide + 2,
      // Preload 2 ahead for smoother experience
      currentSlide - 1].filter(i => i >= 0 && i < slides.length && !preloadedRef.current.has(i));

      // Preload in parallel with priority to current slide (skip ad slides)
      const priorityPromises = slidesToPreload.map(i_0 => {
        const slide_0 = slides[i_0];
        if (slide_0 && slide_0.imageUrl && !slide_0.isVideo && !slide_0.isAd) {
          preloadedRef.current.add(i_0);
          return preloadImage(slide_0.imageUrl);
        }
        return Promise.resolve();
      });
      await Promise.all(priorityPromises);
    };
    preloadSlides();
  }, [currentSlide, slides]);

  // Preload all images on mount for faster navigation
  useEffect(() => {
    const preloadAllImages = async () => {
      // Preload first 5 images immediately for better UX (skip ad slides)
      const firstBatch = slides.slice(0, 5).filter(s => s.imageUrl && !s.isVideo && !s.isAd);
      await Promise.all(firstBatch.map(s_0 => preloadImage(s_0.imageUrl!)));

      // Then preload rest in background with lower priority (skip ad slides)
      const restBatch = slides.slice(5).filter(s_1 => s_1.imageUrl && !s_1.isVideo && !s_1.isAd);
      // Use requestIdleCallback for non-blocking preloading if available
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => {
          restBatch.forEach(s_2 => preloadImage(s_2.imageUrl!));
        });
      } else {
        // Fallback: preload with setTimeout to avoid blocking
        setTimeout(() => {
          restBatch.forEach(s_3 => preloadImage(s_3.imageUrl!));
        }, 100);
      }
    };
    if (slides.length > 0) {
      preloadAllImages();
    }
  }, [slides]);

  // Handle image load state
  const handleImageLoad = useCallback(() => {
    setIsImageLoading(false);
  }, []);

  // Reset image loading state on slide change
  useEffect(() => {
    const currentSlideData = slides[currentSlide];
    if (currentSlideData && !currentSlideData.isVideo && !currentSlideData.isAd && currentSlideData.imageUrl) {
      // Check if already cached
      if (imageCache.has(currentSlideData.imageUrl)) {
        setIsImageLoading(false);
      } else {
        setIsImageLoading(true);
      }
    } else {
      setIsImageLoading(false);
    }
  }, [currentSlide, slides]);

  // Fetch stories from section collection
  useEffect(() => {
    const fetchCollectionStories = async () => {
      const sectionSlug = initialStory.sections?.[0]?.slug;
      if (!sectionSlug) return;
      try {
        const response = await fetch(`${API_PROXY_PATH}/collections/${sectionSlug}`);
        const data = await response.json();
        const stories = data?.items?.filter((item: any) => item.type === 'story' && item.story?.slug && item.story['story-template'] === 'visual-story')?.map((item_0: any) => ({
          slug: item_0.story.slug,
          story: item_0.story
        })) || [];
        setAllStories(stories);
        const index_0 = stories.findIndex((s_4: StoryData) => s_4.slug === initialStory.slug);
        setCurrentStoryIndex(index_0 !== -1 ? index_0 : 0);
      } catch (error) {
        console.error('Failed to fetch collection:', error);
      }
    };
    fetchCollectionStories();
  }, [initialStory.slug, initialStory.sections]);
  const totalSlides = slides.length;
  const hasPrevStory = currentStoryIndex > 0;
  const hasNextStory = currentStoryIndex < allStories.length - 1 && currentStoryIndex !== -1;
  const goToPrevStory = useCallback(() => {
    if (!hasPrevStory || isLoading) return;
    setIsLoading(true);
    preloadedRef.current.clear();
    const prevIndex = currentStoryIndex - 1;
    const prevStory = allStories[prevIndex];
    const newUrl = `/${prevStory.slug}`;
    window.history.pushState(null, '', newUrl);
    setCurrentStory(prevStory.story);
    setCurrentStoryIndex(prevIndex);
    setCurrentSlide(0);
    setStoriesNavigated(prev => prev + 1);
    setIsLoading(false);
  }, [hasPrevStory, currentStoryIndex, allStories, isLoading]);
  const goToNextStory = useCallback(() => {
    if (!hasNextStory || isLoading) return;
    setIsLoading(true);
    preloadedRef.current.clear();
    const nextIndex = currentStoryIndex + 1;
    const nextStory = allStories[nextIndex];
    const newUrl_0 = `/${nextStory.slug}`;
    window.history.pushState(null, '', newUrl_0);
    setCurrentStory(nextStory.story);
    setCurrentStoryIndex(nextIndex);
    setCurrentSlide(0);
    setStoriesNavigated(prev_0 => prev_0 + 1);
    setIsLoading(false);
  }, [hasNextStory, currentStoryIndex, allStories, isLoading]);
  const handlePrevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  }, [currentSlide]);
  const handleNextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  }, [currentSlide, totalSlides]);
  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  // Handle close button - go back to original page (before opening VisualStory)
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }

    // Clear sessionStorage
    sessionStorage.removeItem('visualStoryReturnUrl');
    sessionStorage.removeItem('navigationIntent');
    sessionStorage.removeItem('pendingStoryTemplate');

    // Navigate to the stored return URL (the page user was on before opening story)
    const returnUrl = returnUrlRef.current || '/';
    window.location.href = returnUrl;
  }, [onClose]);
  const togglePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPaused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
      setIsPaused(!isPaused);
    }
  }, [isPaused]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevSlide();
      } else if (e.key === 'ArrowRight') {
        handleNextSlide();
      } else if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevSlide, handleNextSlide, handleClose]);
  if (slides.length === 0) {
    return null;
  }
  const currentSlideData_0 = slides[currentSlide];
  return <div className="fixed inset-0 z-[9999] overflow-hidden bg-black">
      {/* Blurred Background - Use cached image */}
      <div className="absolute inset-0">
        {currentSlideData_0.isAd || currentSlideData_0.isVideo ? <div className="w-full h-full bg-gray-900" /> : <div className="w-full h-full bg-cover bg-center blur-3xl scale-150 opacity-50" style={{
        backgroundImage: `url(${currentSlideData_0.imageUrl})`
      }} />}
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Main Container */}
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Left Arrow Button */}
        <button onClick={handlePrevSlide} className={`absolute left-2 sm:left-4 lg:left-[calc(50%-280px)] z-30 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white transition-opacity ${currentSlide === 0 ? 'opacity-30' : 'opacity-100 hover:bg-white/30'}`} disabled={currentSlide === 0} aria-label="Previous Slide">
          <ChevronLeft size={20} strokeWidth={2} />
        </button>

        {/* Visual Story Container */}
        <div className="relative w-full max-w-[380px] sm:max-w-[500px] h-full bg-black flex flex-col shadow-2xl overflow-hidden">
          {/* Progress Bar */}
          <div className="absolute top-0 left-0 right-0 z-30 px-3 pt-3 flex gap-[3px]">
            {slides.map((_, index_1) => <div key={index_1} className="h-[4px] flex-1 rounded-full overflow-hidden bg-white/30">
                <div className={`h-full rounded-full transition-all duration-300 ${index_1 <= currentSlide ? 'w-full bg-FFDB59' : 'w-0 bg-transparent'}`} />
              </div>)}
          </div>

          {/* Top Controls */}
          <div className="absolute top-10 right-3 z-30 flex items-center gap-2">
            {currentSlideData_0.isVideo && <>
                <button onClick={toggleMute} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white transition-all hover:bg-white/30" aria-label={isMuted ? "Unmute" : "Mute"}>
                  {isMuted ? <VolumeX size={20} strokeWidth={2} /> : <Volume2 size={20} strokeWidth={2} />}
                </button>
                <button onClick={togglePlayPause} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white transition-all hover:bg-white/30" aria-label={isPaused ? "Play" : "Pause"}>
                  {isPaused ? <Play size={20} strokeWidth={2} /> : <Pause size={20} strokeWidth={2} />}
                </button>
              </>}
            {/* {onClose && (
              <button
                onClick={handleClose}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white transition-all hover:bg-white/30"
                aria-label="Close"
              >
                <X size={20} strokeWidth={2} />
              </button>
             )} */}
          </div>

          {/* Media Container */}
          <div className="relative flex-1 w-full overflow-hidden">
            <div className="relative w-full h-full">
              {/* Loading Spinner for Image */}
              {isImageLoading && !currentSlideData_0.isVideo && !currentSlideData_0.isAd && <div className="absolute inset-0 z-10 flex items-center justify-center bg-black">
                  <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                </div>}

              {/* Dinamani Logo - Top Left on First Card */}
              {currentSlide === 0 && !currentSlideData_0.isAd && <div className="absolute top-12 left-3 z-20">
                  <img src="https://media.dinamani.com/dinamani/2025-07-25/xsbo4ylj/DinamaniLogo.png" alt="தினமணி" className="h-8 sm:h-10 w-auto drop-shadow-lg" />
                </div>}

              {currentSlideData_0.isAd ? <SliderAdSlot adCode={currentSlideData_0.adCode || null} adIndex={currentSlideData_0.adIndex || 0} /> : currentSlideData_0.isVideo ? <video key={currentSlideData_0.videoUrl} ref={videoRef} src={currentSlideData_0.videoUrl} className="w-full h-full object-cover" autoPlay muted={isMuted} loop playsInline onPause={() => setIsPaused(true)} onPlay={() => setIsPaused(false)} /> : <img key={currentSlideData_0.imageUrl} src={currentSlideData_0.imageUrl} alt={currentSlideData_0.title} className={`w-full h-full object-cover transition-opacity duration-200 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`} style={currentSlideData_0.focusX !== undefined && currentSlideData_0.focusY !== undefined ? {
              objectPosition: `${currentSlideData_0.focusX}% ${currentSlideData_0.focusY}%`
            } : undefined} onLoad={handleImageLoad} loading="eager" decoding="async" />}
            </div>

            {/* Gradient Overlay - hide for ad slides */}
            {!currentSlideData_0.isAd && <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{
            height: '55%',
            background: 'linear-gradient(0deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 35%, rgba(0,0,0,0.4) 65%, transparent 100%)'
          }} />}
          </div>

          {/* Bottom Text, CTA & Attribution - hide for ad slides */}
          {!currentSlideData_0.isAd && (currentSlideData_0.title || currentSlideData_0.attribution || currentSlideData_0.ctaTitle || currentSlideData_0.authorName || currentSlideData_0.isLastCard) && <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-4 pt-6">
              {/* Last card: promotional message */}
              {currentSlideData_0.isLastCard && currentSlideData_0.promotionalMessage && <div className="text-white/90 text-[10px] sm:text-[10px] leading-[1.3] [&_a]:text-blue-400 [&_a]:underline [&_p]:mb-2" dangerouslySetInnerHTML={{
            __html: currentSlideData_0.promotionalMessage
          }} />}
              {/* Regular slides: title text */}
              {!currentSlideData_0.isLastCard && currentSlideData_0.title && <h1 className="text-white text-[15px] sm:text-[17px] leading-[1.6] font-medium article-links [&_a]:text-blue-400 [&_a]:underline [&_p]:mb-2" dangerouslySetInnerHTML={{
            __html: processArticleLinks(currentSlideData_0.title)
          }} />}
              {/* CTA button - last card uses API ctaUrl/ctaTitle, others use ctaUrl */}
              {currentSlideData_0.isLastCard && <a href={currentSlideData_0.ctaUrl || "/webstories"} className="inline-flex items-center gap-2 mt-3 px-5 py-2 bg-white/20 backdrop-blur-sm text-white text-[13px] sm:text-[14px] font-medium rounded-full border border-white/30 hover:bg-white/30 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                  {currentSlideData_0.ctaTitle || "முகப்பு பக்கத்துக்கு..."}
                </a>}
              {!currentSlideData_0.isLastCard && currentSlideData_0.ctaTitle && currentSlideData_0.ctaUrl && <a href={currentSlideData_0.ctaUrl} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 px-5 py-2 bg-white/20 backdrop-blur-sm text-white text-[13px] sm:text-[14px] font-medium rounded-full border border-white/30 hover:bg-white/30 transition-colors">
                  {currentSlideData_0.ctaTitle} →
                </a>}
              {/* Author name - bottom right on first slide only */}
              {currentSlide === 0 && currentSlideData_0.authorName && <div className="flex justify-end mt-1">
                  <p className="text-FFDB59 text-[12px] sm:text-[13px] font-medium">
                    {currentSlideData_0.authorName}
                  </p>
                </div>}
            </div>}

          {/* Bottom Navigation - Story navigation hidden, only slide navigation available */}
          {/* <div className="absolute bottom-0 left-0 right-0 z-30 flex items-center justify-between px-4 pb-3">
            <button
              onClick={goToPrevStory}
              className={`w-9 h-9 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white transition-all ${
                !hasPrevStory || isLoading ? 'opacity-30 cursor-not-allowed' : 'opacity-100 hover:bg-white/30 cursor-pointer'
              }`}
              disabled={!hasPrevStory || isLoading}
              aria-label="Previous Story"
            >
              <ChevronLeft size={20} strokeWidth={2} />
            </button>
            <button
              onClick={goToNextStory}
              className={`w-9 h-9 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white transition-all ${
                !hasNextStory || isLoading ? 'opacity-30 cursor-not-allowed' : 'opacity-100 hover:bg-white/30 cursor-pointer'
              }`}
              disabled={!hasNextStory || isLoading}
              aria-label="Next Story"
            >
              <ChevronRight size={20} strokeWidth={2} />
            </button>
           </div> */}

          {/* Touch Areas */}
          <div className="absolute top-0 left-0 right-0 bottom-16 z-10 flex pointer-events-none">
            <div className="w-1/3 h-full pointer-events-auto cursor-pointer" onClick={handlePrevSlide} />
            <div className="w-1/3 h-full" />
            <div className="w-1/3 h-full pointer-events-auto cursor-pointer" onClick={handleNextSlide} />
          </div>

          {/* Preload next slides - hidden images (skip ad slides) */}
          <div className="hidden" aria-hidden="true">
            {slides.slice(currentSlide + 1, currentSlide + 3).map((slide_1, i_1) => slide_1.imageUrl && !slide_1.isVideo && !slide_1.isAd && <img key={i_1} src={slide_1.imageUrl} alt="" />)}
          </div>
        </div>

        {/* Right Arrow / Refresh Button */}
        <button onClick={currentSlide === totalSlides - 1 ? () => setCurrentSlide(0) : handleNextSlide} className="absolute right-2 sm:right-4 lg:right-[calc(50%-280px)] z-30 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm text-white opacity-100 hover:bg-white/30 transition-opacity" aria-label={currentSlide === totalSlides - 1 ? 'Replay story' : 'Next Slide'}>
          {currentSlide === totalSlides - 1 ? <RotateCcw size={18} strokeWidth={2} /> : <ChevronRight size={20} strokeWidth={2} />}
        </button>
      </div>

      {/* Loading Overlay */}
      {isLoading && <div className="absolute inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>}
    </div>;
}